// utils/predictorAuthApi.ts
// CLEAN VERSION - Authentication Only (Proxy Routes Only)

// NO API_BASE_URL needed - we use Next.js proxy routes exclusively

export interface PredictorLoginRequest {
    username: string;
    password: string;
  }
  
  export interface PredictorLoginResponse {
    success: boolean;
    message: string;
    user: {
      id: string;
      username: string;
      fullName: string;
      email: string;
      cohort: 'TO' | 'GP';
      groupNumber: number;
      credentialType: 'before' | 'after';
    };
  }
  
  export interface AuthError {
    message: string;
    statusCode: number;
    error?: string;
  }
  
  export interface AuthStatus {
    isAuthenticated: boolean;
    user?: any;
  }
  
  /**
   * Check if predictor is authenticated with enhanced debugging
   */
  export async function checkAuth(): Promise<AuthStatus> {
    try {
      console.log('🔍 [AUTH] Checking authentication status...');
      console.log('🍪 [AUTH] Current cookies:', document.cookie);
      
      // Use proxy route for consistency
      const response = await fetch('/api/kom/auth/status', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
  
      console.log('📡 [AUTH] Response status:', response.status);
      console.log('📡 [AUTH] Response headers:', Object.fromEntries(response.headers.entries()));
  
      if (!response.ok) {
        const errorText = await response.text();
        console.warn('❌ [AUTH] Authentication check failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        return { isAuthenticated: false };
      }
  
      const result = await response.json();
      console.log('✅ [AUTH] Authentication check successful:', result);
      return { 
        isAuthenticated: result.success, 
        user: result.user 
      };
    } catch (error) {
      console.error('🚨 [AUTH] Auth check failed:', error);
      return { isAuthenticated: false };
    }
  }
  
  /**
   * Login a predictor with username/password
   */
  export async function loginPredictor(
    username: string, 
    password: string
  ): Promise<PredictorLoginResponse> {
    try {
      console.log('🔐 [LOGIN] Attempting predictor login for:', username);
      
      // Use the Next.js proxy route for better CORS handling
      const response = await fetch('/api/kom/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: Include cookies for session management
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });
  
      console.log('📡 [LOGIN] Response status:', response.status);
      console.log('📡 [LOGIN] Response ok:', response.ok);
      console.log('📡 [LOGIN] Response headers:', Object.fromEntries(response.headers.entries()));
  
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
          console.log('❌ [LOGIN] Error response data:', errorData);
        } catch (parseError) {
          console.error('❌ [LOGIN] Failed to parse error response:', parseError);
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        throw {
          message: errorData.message || 'Login failed',
          statusCode: response.status,
          error: errorData.error || 'Authentication Error',
        } as AuthError;
      }
  
      const data = await response.json();
      console.log('✅ [LOGIN] Successful login response:', data);
      console.log('🍪 [LOGIN] Cookies after login:', document.cookie);
      return data as PredictorLoginResponse;
    } catch (error) {
      console.error('🚨 [LOGIN] Login function caught error:', error);
      
      // Network or parsing errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw {
          message: 'Unable to connect to the server. Please check your internet connection.',
          statusCode: 0,
          error: 'Network Error',
        } as AuthError;
      }
      
      // Re-throw API errors
      throw error;
    }
  }
  
  /**
   * Logout current user (clears session cookie)
   */
  export async function logoutPredictor(): Promise<void> {
    try {
      console.log('🚪 [LOGOUT] Attempting logout...');
      await fetch('/api/kom/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      console.log('✅ [LOGOUT] Logout successful');
    } catch (error) {
      console.warn('⚠️ [LOGOUT] Logout request failed:', error);
      // Don't throw - logout should always succeed from UI perspective
    }
  }
  
  /**
   * Get current user session data
   */
  export async function getCurrentUser(): Promise<any> {
    try {
      console.log('👤 [USER] Fetching current user...');
      const response = await fetch('/api/kom/auth/me', {
        method: 'GET',
        credentials: 'include',
      });
  
      if (!response.ok) {
        console.log('❌ [USER] Get current user failed - not authenticated');
        return null;
      }
  
      const data = await response.json();
      console.log('✅ [USER] Current user fetched:', data);
      return data;
    } catch (error) {
      console.warn('⚠️ [USER] Get current user failed:', error);
      return null;
    }
  }
  
  /**
   * Check if error is a credentials error (401)
   */
  export function isCredentialsError(error: any): boolean {
    return error?.statusCode === 401 || 
           error?.message?.toLowerCase().includes('invalid credentials') ||
           error?.message?.toLowerCase().includes('unauthorized');
  }
  
  /**
   * Check if error is a network error
   */
  export function isNetworkError(error: any): boolean {
    return error?.statusCode === 0 || 
           error?.error === 'Network Error' ||
           error?.message?.toLowerCase().includes('network') ||
           error?.message?.toLowerCase().includes('connection');
  }
  
  /**
   * Check if error is a server error (5xx)
   */
  export function isServerError(error: any): boolean {
    return error?.statusCode >= 500;
  }
  
  /**
   * Get user-friendly error message
   */
  export function getErrorMessage(error: any): string {
    if (isNetworkError(error)) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (isCredentialsError(error)) {
      return 'Invalid username or password. Please check your credentials and try again.';
    }
    
    if (isServerError(error)) {
      return 'Server is temporarily unavailable. Please try again in a few moments.';
    }
    
    return error?.message || 'An unexpected error occurred. Please try again.';
  }
  
  /**
   * Validate username format (can be email or username)
   */
  export function isValidUsername(username: string): boolean {
    if (!username || username.trim().length === 0) {
      return false;
    }
    
    const trimmed = username.trim();
    
    // Allow email format
    if (trimmed.includes('@')) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(trimmed);
    }
    
    // Allow username format (alphanumeric, underscore, dash, min 3 chars)
    const usernameRegex = /^[a-zA-Z0-9_-]{3,}$/;
    return usernameRegex.test(trimmed);
  }
  
  /**
   * Validate password strength
   */
  export function isValidPassword(password: string): boolean {
    return password && password.length >= 8;
  }
  
  /**
   * Get password validation message
   */
  export function getPasswordValidationMessage(password: string): string | null {
    if (!password) {
      return 'Password is required';
    }
    
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    
    return null;
  }
  
  /**
   * Get username validation message
   */
  export function getUsernameValidationMessage(username: string): string | null {
    if (!username || username.trim().length === 0) {
      return 'Username or email is required';
    }
    
    if (!isValidUsername(username)) {
      if (username.includes('@')) {
        return 'Please enter a valid email address';
      } else {
        return 'Username must be at least 3 characters and contain only letters, numbers, underscores, or dashes';
      }
    }
    
    return null;
  }